import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { FinancialMetricsProps, ReportData } from '../tokens/types';
import { PdfSection } from '../primitives/PdfPrimitives';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

const fmtCell = (v: number | string | null, unit?: string): string => {
    if (v == null || v === '') return '—';
    if (typeof v === 'number') {
        const formatted = v.toLocaleString('en-IN', { maximumFractionDigits: unit === '%' ? 1 : 2 });
        return unit ? `${formatted}${unit}` : formatted;
    }
    return String(v);
};

const dataColumns = (fm: FinancialMetricsProps) => fm.headers.filter((h) => h.key !== 'metricName').slice(-4);

// ──────────────────────────── PDF ────────────────────────────

const PdfFinancialMetrics: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const fm = data.financialMetrics;
    if (!fm) return null;
    const cols = dataColumns(fm);
    const colWidth = `${Math.floor(66 / cols.length)}%`;
    return (
        <PdfSection kicker="Financials" title="Key financial metrics" index={index} breakBefore={breakBefore}>
            <View style={{ marginTop: space.sp2, borderTopWidth: 0.5, borderTopColor: colors.ink00 }}>
                <View
                    style={{
                        flexDirection: 'row',
                        paddingVertical: 5,
                        borderBottomWidth: 0.5,
                        borderBottomColor: colors.ink00,
                        backgroundColor: colors.surfaceAlt,
                    }}
                    fixed
                >
                    <Text
                        style={{
                            width: '34%',
                            fontSize: typeScale.micro,
                            fontFamily: fontFamily.bodyBold,
                            color: colors.ink40,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            paddingHorizontal: 4,
                        }}
                    >
                        Metric
                    </Text>
                    {cols.map((c) => (
                        <Text
                            key={c.key}
                            style={{
                                width: colWidth,
                                fontSize: typeScale.micro,
                                fontFamily: fontFamily.bodyBold,
                                color: colors.ink40,
                                textAlign: 'right',
                                paddingHorizontal: 4,
                            }}
                        >
                            {c.name}
                        </Text>
                    ))}
                </View>
                {fm.rows.map((row, ri) => (
                    <View
                        key={ri}
                        wrap={false}
                        style={{
                            flexDirection: 'row',
                            // Per PM feedback: rows felt cramped at paddingVertical 4 —
                            // bumped to 7 so values breathe vertically without changing
                            // the page-density math elsewhere in the section.
                            paddingVertical: 7,
                            paddingHorizontal: 4,
                            borderBottomWidth: 0.5,
                            borderBottomColor: colors.rule,
                            backgroundColor: ri % 2 === 0 ? colors.paper : colors.surfaceAlt,
                        }}
                    >
                        {/* skill rule 9 corollary: sans label + mono value siblings — lineHeight:1 prevents baseline drift */}
                        <Text
                            style={{
                                width: '34%',
                                fontSize: typeScale.body,
                                // Dialled metric column from bodyBold (700) → sans@500.
                                // The bold treatment was visually competing with the
                                // numeric values to its right; medium weight keeps the
                                // metric name readable without shouting.
                                fontFamily: fontFamily.body,
                                fontWeight: 500,
                                color: colors.ink00,
                                lineHeight: 1,
                            }}
                        >
                            {row.metricName}
                        </Text>
                        {cols.map((c) => (
                            <Text
                                key={c.key}
                                style={{
                                    width: colWidth,
                                    fontSize: typeScale.body,
                                    color: colors.ink10,
                                    textAlign: 'right',
                                    fontFamily: fontFamily.mono,
                                    lineHeight: 1,
                                }}
                            >
                                {fmtCell(row.values[c.key], row.unit)}
                            </Text>
                        ))}
                    </View>
                ))}
            </View>
        </PdfSection>
    );
};

// ──────────────────────────── Screen ────────────────────────────

const ScreenFinancialMetrics: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const fm = data.financialMetrics;
    if (!fm) return null;
    const cols = dataColumns(fm);
    const gridCols = `2fr repeat(${cols.length}, 1fr)`;
    return (
        <ScreenSection kicker="Financials" title="Key financial metrics" index={index}>
            <div style={{ marginTop: 8, borderTop: `1px solid ${colors.ink00}`, overflowX: 'auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: gridCols,
                        padding: '6px 8px',
                        borderBottom: `1px solid ${colors.ink00}`,
                        background: colors.surfaceAlt,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: colors.ink40,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        Metric
                    </div>
                    {cols.map((c) => (
                        <div
                            key={c.key}
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: colors.ink40,
                                textAlign: 'right',
                            }}
                        >
                            {c.name}
                        </div>
                    ))}
                </div>
                {fm.rows.map((row, ri) => (
                    <div
                        key={ri}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: gridCols,
                            // Bumped 5 → 9px vertical for the same row-breathing reason
                            // the PDF side was bumped to 7pt.
                            padding: '9px 8px',
                            borderBottom: `1px solid ${colors.rule}`,
                            background: ri % 2 === 0 ? colors.paper : colors.surfaceAlt,
                        }}
                    >
                        <div style={{ fontSize: 12, fontWeight: 500, color: colors.ink00 }}>{row.metricName}</div>
                        {cols.map((c) => (
                            <div
                                key={c.key}
                                style={{
                                    fontSize: 12,
                                    color: colors.ink10,
                                    textAlign: 'right',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {fmtCell(row.values[c.key], row.unit)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </ScreenSection>
    );
};

export const financialMetricsModule: ModuleDefinition = {
    meta: {
        id: 'financialMetrics',
        title: 'Key financial metrics',
        description: 'Revenue, margins, working capital, debt and tangible net worth overview.',
        dataSource: 'Audited Financials (financial metrics)',
    },
    // Presence of rows is not enough — Polus often returns the row scaffolding
    // (Revenue / EBITDA / etc.) with every cell null AND no data-year columns.
    // Render only when (a) there's at least one non-metricName column to fill
    // and (b) at least one cell has an actual value.
    hasData: (d) => {
        const fm = d.financialMetrics;
        if (!fm || fm.rows.length === 0) return false;
        const dataCols = fm.headers.filter((h) => h.key !== 'metricName');
        if (dataCols.length === 0) return false;
        return fm.rows.some((row) => Object.values(row.values ?? {}).some((v) => v != null && v !== ''));
    },
    Pdf: PdfFinancialMetrics,
    Screen: ScreenFinancialMetrics,
};
